import {
  EntityType,
  type UploadMediaCommandInput,
  type UploadMediaCommandOutput,
} from '@amzn/gbsa-helios-typescript-client';
import { fileToUploadMediaInput, type UploadMediaContext } from '../../../shared/data';
import type { MediaIdsMap } from 'src/types/helios/Question';
import type { CriteriaAttachments, CriteriaField } from '../components/AuditCriteriaStep';

/** Identity fields stamped onto each uploaded attachment. `entityType` is fixed
 *  to QUESTION here — these attachments belong to the question being published. */
export type CriteriaAttachmentContext = Omit<UploadMediaContext, 'entityType'>;

/** The five criteria fields, in a stable order, so the upload loop is
 *  deterministic. Mirrors the `CriteriaField` union. */
const CRITERIA_FIELDS: readonly CriteriaField[] = [
  'questionText',
  'auditInstruction',
  'passCriteria',
  'failCriteria',
  'justification',
];

/**
 * Uploads the discrete per-criteria-field attachment files for a question and
 * returns a {@link MediaIdsMap} keyed by criteria field (the field keys are a
 * subset of `MediaIdsMap`'s keys, so the mapping is 1:1). A field with no files
 * is omitted from the map rather than emitting an empty array.
 *
 * Pure orchestration over the shared media primitives: each `File` is turned
 * into an upload command input by {@link fileToUploadMediaInput} (which also
 * rejects unsupported types) and sent through the injected `uploadMedia`
 * mutation. Uploads run concurrently; every upload must return a media id or
 * the whole publish fails (a silently-dropped attachment is worse than a
 * failed save the user can retry).
 *
 * This handles ONLY discrete attached files. Inline base64 images embedded in
 * the rich-text fields are uploaded separately via `uploadDescriptionImages`
 * (which also rewrites the HTML); the publish orchestrator merges both maps.
 */
export async function uploadCriteriaAttachments(
  attachments: CriteriaAttachments,
  context: CriteriaAttachmentContext,
  uploadMedia: (input: UploadMediaCommandInput) => Promise<UploadMediaCommandOutput>,
): Promise<MediaIdsMap> {
  const uploadContext: UploadMediaContext = {
    ...context,
    entityType: EntityType.QUESTION,
  };

  // Upload every field's files concurrently, tagging each result with its
  // field so the flat result list can be regrouped into the per-field map.
  // Iterate the attachments as typed entries (not a dynamic object index by a
  // variable key) and keep only the known criteria fields, so there's no
  // object-injection sink.
  const CRITERIA_FIELD_SET = new Set<string>(CRITERIA_FIELDS);
  const entries = (Object.entries(attachments) as [CriteriaField, File[]][]).filter(
    ([field]) => CRITERIA_FIELD_SET.has(field),
  );
  const perFileUploads = entries.flatMap(([field, files]) =>
    files.map(async (file) => {
      const input = await fileToUploadMediaInput(file, uploadContext);
      const result = await uploadMedia(input);
      const mediaId = result.media?.mediaId;
      if (!mediaId) {
        throw new Error('Attachment upload did not return a media ID');
      }
      return { field, mediaId };
    }),
  );

  const uploaded = await Promise.all(perFileUploads);

  // Regroup into a Map (keeps the variable-keyed writes off the
  // object-injection sink), preserving upload order within each field and
  // omitting fields that had no attachments, then materialize the MediaIdsMap
  // in one typed `Object.fromEntries` (no per-key dynamic object write).
  const idsByField = new Map<CriteriaField, string[]>();
  for (const { field, mediaId } of uploaded) {
    const existing = idsByField.get(field);
    if (existing) {
      existing.push(mediaId);
    } else {
      idsByField.set(field, [mediaId]);
    }
  }
  return Object.fromEntries(idsByField);
}
